import React, { memo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
} from '@mui/icons-material';

const DataTable = ({
  title,
  data = [],
  columns = [],
  loading = false,
  error = null,
  onAdd,
  onEdit,
  onDelete,
  onView,
  showActions = true,
  disableEdit = false,
  disableDelete = false,
  disableView = false,
  emptyMessage = 'No data available',
  sx = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  const normalizedData = Array.isArray(data) ? data : [];

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%', 
      overflow: 'hidden',
      position: 'relative',
      ...sx 
    }}>
      {title && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: { xs: 1.5, md: 2 },
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Typography 
            variant="h5" 
            component="h1"
            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}
          >
            {title}
          </Typography>
          {onAdd && (
            <IconButton
              color="primary"
              onClick={onAdd}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              <AddIcon fontSize={isMobile ? 'small' : 'medium'} />
            </IconButton>
          )}
        </Box>
      )}

      <TableContainer 
        component={Paper} 
        sx={{ 
          boxShadow: 1,
          overflowX: { xs: 'auto', md: 'hidden' },
          overflowY: 'visible',
          width: '100%',
          maxWidth: { xs: 'calc(100vw - 32px)', md: '100%' },
          display: { xs: 'block', md: 'table' },
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': {
            height: 10,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'grey.100',
            borderRadius: 5,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'grey.400',
            borderRadius: 5,
            border: '2px solid',
            borderColor: 'grey.100',
            '&:hover': {
              backgroundColor: 'grey.500',
            },
          },
          // Firefox scrollbar styling
          scrollbarWidth: 'thin',
          scrollbarColor: '#9ca3af #f3f4f6',
        }}
      >
        <Table 
          sx={{ 
            minWidth: { xs: 600, md: 0 },
            width: { xs: 'max-content', md: '100%' },
            tableLayout: { xs: 'auto', md: 'auto' },
            '& .MuiTableCell-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
              padding: { xs: '6px 4px', sm: '12px 8px', md: '16px' },
            },
          }} 
          aria-label="data table"
        >
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              {columns.map((column, index) => (
                <TableCell
                  key={index}
                  align={column.align || 'left'}
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    whiteSpace: 'nowrap',
                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                    padding: { xs: '8px 4px', sm: '12px 8px', md: '16px' },
                    width: column.width,
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                  }}
                >
                  {column.header}
                </TableCell>
              ))}
              {showActions && (
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                    padding: { xs: '8px 4px', sm: '12px 8px', md: '16px' },
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {normalizedData.length === 0 ? (
              <TableRow>
                <TableCell 
                  colSpan={columns.length + (showActions ? 1 : 0)} 
                  align="center" 
                  sx={{ 
                    py: { xs: 3, md: 4 },
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              normalizedData.map((row, rowIndex) => (
                <TableRow
                  key={row.id || rowIndex}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  {columns.map((column, colIndex) => (
                    <TableCell 
                      key={colIndex}
                      align={column.align || 'left'}
                      sx={{ 
                        py: { xs: 1, sm: 1.25, md: 1.5 },
                        px: { xs: 0.5, sm: 1, md: 2 },
                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' },
                        whiteSpace: { xs: 'nowrap', md: 'normal' },
                        width: column.width,
                        minWidth: column.minWidth,
                        maxWidth: column.maxWidth,
                      }}
                    >
                      {column.render ? (
                        column.render(row[column.field], row)
                      ) : (
                        <Typography 
                          variant="body2"
                          sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: { xs: 120, sm: 200, md: 'none' },
                          }}
                        >
                          {row[column.field] || '-'}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                  {showActions && (
                    <TableCell 
                      align="center" 
                      sx={{ 
                        py: { xs: 1, sm: 1.25, md: 1.5 },
                        px: { xs: 0.5, sm: 1, md: 2 },
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        gap: { xs: 0.25, sm: 0.5 }, 
                        justifyContent: 'center',
                        flexWrap: 'nowrap',
                      }}>
                        {onView && !disableView && (
                          <Tooltip title="View">
                            <IconButton
                              size={isMobile ? 'small' : 'small'}
                              color="info"
                              onClick={() => onView(row)}
                              sx={{ 
                                padding: { xs: '4px', sm: '8px' },
                              }}
                            >
                              <ViewIcon fontSize={isMobile ? 'small' : 'small'} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onEdit && !disableEdit && (
                          <Tooltip title="Edit">
                            <IconButton
                              size={isMobile ? 'small' : 'small'}
                              color="primary"
                              onClick={() => onEdit(row)}
                              sx={{ 
                                padding: { xs: '4px', sm: '8px' },
                              }}
                            >
                              <EditIcon fontSize={isMobile ? 'small' : 'small'} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onDelete && !disableDelete && (
                          <Tooltip title="Delete">
                            <IconButton
                              size={isMobile ? 'small' : 'small'}
                              color="error"
                              onClick={() => onDelete(row)}
                              sx={{ 
                                padding: { xs: '4px', sm: '8px' },
                              }}
                            >
                              <DeleteIcon fontSize={isMobile ? 'small' : 'small'} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// Memoize DataTable to prevent unnecessary re-renders when parent updates
export default memo(DataTable, (prevProps, nextProps) => {
  // Only re-render if these critical props change
  return (
    prevProps.loading === nextProps.loading &&
    prevProps.error === nextProps.error &&
    JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) &&
    JSON.stringify(prevProps.columns) === JSON.stringify(nextProps.columns)
  );
});
